import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiIcon,
  EuiPageTemplate,
} from "@inscopix/ideas-eui";
import { ProjectTabFiles } from "components/ProjectTabs/ProjectTabFiles";
import { ProjectTabTasks } from "components/ProjectTabs/ProjectTabTasks";
import { RoleBadge } from "components/RoleBadge/RoleBadge";
import { Tenant, Tool } from "graphql/_Types";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import {
  DynamicRoute,
  useRouteMapContext,
} from "providers/RouteMapProvider/RouteMapProvider";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { memo, useMemo } from "react";
import { Route, Switch } from "react-router-dom";
import { useLocation } from "react-router-dom-v5-compat";
import { ProjectPageContent } from "pages/gdt/layout/ProjectPageContent";
import { ToolTabTool } from "components/ToolProjectTabs/ToolTabTool";
import { ToolTabSettings } from "components/ToolProjectTabs/ToolTabSettings";
import { ToolBreadcrumbs } from "components/HeaderBreadcrumbs/ToolBreadCrumbs";
import { useSetAppHeader } from "providers/AppHeaderProvider";
import { useToolDataContext } from "./ToolDataProvider";

export type ToolTabName = "Tool" | "Tasks" | "Files" | "Settings";

interface ToolTab {
  name: ToolTabName;
  icon: string;
  component: typeof ToolTabTool;
  route: DynamicRoute<
    string,
    {
      tenantKey: Tenant["key"];
      toolId: Tool["id"];
    },
    unknown
  >;
  id: string;
}

/*
 * Tool project home page with tab layout
 */
export const PageToolProjectTabs = memo(function PageToolProjectTabs() {
  const { tool, invalidateToolQuery } = useToolDataContext();
  const currentTenant = useTenantContext((s) => s.currentTenant);
  const pathParams = {
    tenantKey: currentTenant.key,
    toolId: tool.id,
  };
  const role = useProjectPermissionStore((s) => s.permissions.role_display);
  const routeMap = useRouteMapContext((s) => s.routeMap);

  // set header
  const breadcrumbs = useMemo(() => {
    return (
      <ToolBreadcrumbs
        key={"breadcrumbs"}
        tool={tool}
        currentTenant={currentTenant}
        inDraftMode={false}
        invalidateToolQuery={() => void invalidateToolQuery()}
      />
    );
  }, [tool, currentTenant, invalidateToolQuery]);
  useSetAppHeader({ header: breadcrumbs });

  const toolTabs: ToolTab[] = useMemo(
    () => [
      {
        name: "Tool" as const,
        icon: "wrench",
        component: ToolTabTool,
        route: routeMap.TOOL,
        id: "toolVersions",
      },
      {
        name: "Tasks" as const,
        icon: "compute",
        component: ProjectTabTasks,
        route: routeMap.TOOL_TASKS,
        id: "toolTasks",
      },
      {
        name: "Files" as const,
        icon: "documents",
        component: ProjectTabFiles,
        route: routeMap.TOOL_FILES,
        id: "toolFiles",
      },
      {
        name: "Settings" as const,
        icon: "gear",
        component: ToolTabSettings,
        route: routeMap.TOOL_SETTINGS,
        id: "toolSettings",
      },
    ],
    [routeMap],
  );

  const location = useLocation();

  const tabOptions = toolTabs.map((tab) => {
    const dynamicPath = tab.route.dynamicPath(pathParams);
    return {
      label: tab.name,
      isSelected: location.pathname === dynamicPath.path,
      prepend: <EuiIcon type={tab.icon} />,
      onClick: () => {
        dynamicPath.navigateToReplace();
      },
      id: tab.id,
    };
  });

  const tabContent = useMemo(
    () =>
      toolTabs.map(({ id, route, component }) => (
        <Route
          key={id}
          path={route.parameterizedPath}
          exact
          component={component}
        />
      )),
    [toolTabs],
  );

  return (
    <EuiPageTemplate responsive={[]}>
      <ProjectPageContent>
        <EuiPageTemplate.Header
          pageTitle={
            <EuiFlexGroup
              gutterSize={"s"}
              alignItems="center"
              responsive={false}
            >
              <EuiFlexItem grow={false}>
                <EuiText>
                  <h2>{tool.name}</h2>
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <RoleBadge role={role} />
              </EuiFlexItem>
            </EuiFlexGroup>
          }
          tabs={tabOptions}
        />

        <EuiPageTemplate.Section contentProps={{ style: { flexGrow: 1 } }}>
          <Switch>{tabContent}</Switch>
        </EuiPageTemplate.Section>
      </ProjectPageContent>
    </EuiPageTemplate>
  );
});
